import * as React from 'react';
import _ from 'lodash';
import {
  ContainerType,
  ShippingLine,
  SnapshotUrl,
  Terminal,
  TimeRangeSnapshot,
} from 'types/Shipping';
import { getSnapshotUrlsByShippingLine, getTimeRangePerDiem } from 'services/clients/backupBank';

import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { Region } from 'services/clients/regions';
import { useQuery } from '@tanstack/react-query';
import { TimeRangeTerminalTabs } from '../components/TimeRangeTerminalTabs';
import { TerminalTabs } from '../components/TerminalTabs';
import { BACKEND_DATE_FORMAT } from '../config';
import { DateSearchSpec } from '../date-picker';

interface Props {
  terminals: Terminal[];
  shippingLines: ShippingLine[];
  containerTypes: ContainerType[];
  dateSearchSpec: DateSearchSpec;
  selectedShippingLineKey: string;
  selectedContainerTypeKey: string;
  region: Region;
}

function getDateForRequest(dateString: string, timeString: string) {
  const momentDate = moment(`${dateString} ${timeString}`, 'YYYY-MM-DD HH:mm', true);
  if (!momentDate.isValid()) {
    throw new Error(`Invalid date inputs ${dateString} ${timeString}`);
  }

  return momentDate.format();
}

export const Screenshots: React.FC<Props> = (props: Props) => {
  const {
    dateSearchSpec,
    terminals,
    shippingLines,
    containerTypes,
    selectedShippingLineKey,
    selectedContainerTypeKey,
    region,
  } = props;

  const query = useQuery(
    [
      'terminal-screenshots',
      selectedShippingLineKey,
      selectedContainerTypeKey,
      dateSearchSpec,
      region.code,
    ],
    async ({ signal }) => {
      const startDateString = (
        dateSearchSpec.mode === 'specific-date' ? dateSearchSpec.date : dateSearchSpec.startDate
      ).format(BACKEND_DATE_FORMAT);
      const endDateString =
        dateSearchSpec.mode === 'specific-date'
          ? undefined
          : dateSearchSpec.endDate.format(BACKEND_DATE_FORMAT);

      const snapshotUrlsPromise = getSnapshotUrlsByShippingLine(
        selectedShippingLineKey,
        startDateString,
        dateSearchSpec.mode === 'specific-date' ? dateSearchSpec.time : dateSearchSpec.startTime,
        endDateString,
        region.code,
        signal
      ) as Promise<SnapshotUrl[]>;

      let timeRangePromise: Promise<TimeRangeSnapshot[]> = Promise.resolve([]);
      if (dateSearchSpec.mode === 'date-range') {
        const startDate = getDateForRequest(startDateString, dateSearchSpec.startTime || '');
        const endDate = getDateForRequest(endDateString || '', dateSearchSpec.endTime || '');
        timeRangePromise = getTimeRangePerDiem(
          {
            shipping_line: selectedShippingLineKey,
            container_type: selectedContainerTypeKey,
            start_date: startDate,
            end_date: endDate,
            port_collection_codes: [region.code],
          },
          signal
        );
      }

      return Promise.all([snapshotUrlsPromise, timeRangePromise]);
    },
    { retry: false }
  );

  if (query.isLoading) {
    return (
      <div className="h-full w-full bg-white flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (query.isError) {
    return <>Could not find results for selection</>;
  }

  const [snapshotUrls, timeRangeResults] = query.data as [SnapshotUrl[], TimeRangeSnapshot[]];
  let content = null;
  switch (dateSearchSpec.mode) {
    case 'specific-date':
      content = <TerminalTabs ports={terminals} snapshotUrls={snapshotUrls} />;
      break;
    case 'date-range':
      content = (
        <TimeRangeTerminalTabs
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          perDiem={_.groupBy(timeRangeResults, 'port')}
          snapshotUrls={snapshotUrls}
          ports={terminals}
          shippingLines={shippingLines}
          containerTypes={containerTypes}
        />
      );
      break;
    default:
      break;
  }

  return content;
};
Screenshots.displayName = 'Screenshots';
